import React, { useState, useEffect } from 'react';
import { Input, Button } from '../../ui';
import { Plus, Trash2 } from 'lucide-react';

interface SendButtonsConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

// Raw alias text per button index — lets the user type freely (including commas mid-word)
// without the input being controlled/reset on every keystroke.
function useAliasInputs(buttons: any[]) {
  const [rawValues, setRawValues] = useState<Record<number, string>>(() =>
    Object.fromEntries(buttons.map((b, i) => [i, (b.aliases || []).join(', ')]))
  );

  // Sync raw values when buttons array length changes (add/remove)
  useEffect(() => {
    setRawValues(prev => {
      const next: Record<number, string> = {};
      buttons.forEach((b, i) => {
        next[i] = i in prev ? prev[i] : (b.aliases || []).join(', ');
      });
      return next;
    });
  }, [buttons.length]);

  return { rawValues, setRawValues };
}

export const SendButtonsConfig: React.FC<SendButtonsConfigProps> = ({ config, onChange }) => {
  const groupingEnabled = config.messageGroupingEnabled === true;
  const buttons = config.buttons || [];
  const { rawValues, setRawValues } = useAliasInputs(buttons);

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-200 leading-relaxed">
        <strong className="text-blue-800 dark:text-blue-100">Como funciona:</strong> Envia uma mensagem com opções numeradas (1, 2, 3...). O usuário pode responder de três formas:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><strong>Pelo número</strong> — digitando "1", "2", "3", etc.</li>
          <li><strong>Pelo texto</strong> — digitando o nome da opção (ex: "Suporte")</li>
          <li><strong>Por apelido</strong> — digitando um apelido configurado (ex: "sim", "ok")</li>
        </ul>
        <p className="mt-1">A comparação <strong>ignora maiúsculas/minúsculas</strong> (ex: "suporte" = "SUPORTE" = "Suporte"). Se o usuário digitar algo que não corresponde a nenhuma opção ou apelido, a mensagem será repetida pedindo uma resposta válida.</p>
        <p className="mt-2"><strong className="text-blue-800 dark:text-blue-100">Exemplo de uso:</strong></p>
        <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
          <li>Opção 1: <strong>Sim</strong> — Apelidos: <em>s, ok, quero, positivo</em></li>
          <li>Opção 2: <strong>Não</strong> — Apelidos: <em>n, nao, não quero, negativo</em></li>
        </ul>
        <p className="mt-0.5">Se o usuário digitar "ok" ou "positivo", será reconhecido como "Sim".</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          Mensagem
        </label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Digite a mensagem..."
          value={config.message || ''}
          onChange={(e) => onChange('message', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
          Opções do Menu (Lista Numerada)
        </label>
        <div className="space-y-2">
          {buttons.map((button: any, index: number) => (
            <div key={index} className="space-y-1">
              <div className="flex gap-2">
                <div className="flex items-center justify-center w-8 h-10 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300 text-sm font-medium">
                  {index + 1}
                </div>
                <Input
                  placeholder="Texto da opção"
                  value={button.text || ''}
                  onChange={(e) => {
                    const buttons = [...(config.buttons || [])];
                    buttons[index] = { ...buttons[index], text: e.target.value, value: e.target.value };
                    onChange('buttons', buttons);
                  }}
                />
                <button
                  onClick={() => {
                    const buttons = config.buttons.filter(
                      (_: any, i: number) => i !== index
                    );
                    onChange('buttons', buttons);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="ml-10">
                <Input
                  placeholder="Apelidos (separados por vírgula) — ex: sim, ok, quero"
                  value={rawValues[index] ?? (button.aliases || []).join(', ')}
                  onChange={(e) => {
                    setRawValues(prev => ({ ...prev, [index]: e.target.value }));
                  }}
                  onBlur={(e) => {
                    const aliases = e.target.value
                      .split(',')
                      .map((a: string) => a.trim())
                      .filter((a: string) => a.length > 0);
                    const updatedButtons = [...buttons];
                    updatedButtons[index] = { ...updatedButtons[index], aliases };
                    onChange('buttons', updatedButtons);
                    setRawValues(prev => ({ ...prev, [index]: aliases.join(', ') }));
                  }}
                  className="text-xs"
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const buttons = [
                ...(config.buttons || []),
                { id: Date.now().toString(), text: '', value: '' },
              ];
              onChange('buttons', buttons);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Opção
          </Button>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Esta opção enviará uma lista numerada (1, 2, 3...) para garantir compatibilidade com WhatsApp.
          </p>
        </div>
      </div>

      {/* Fallback option */}
      <div className="border-t border-slate-200 pt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.fallbackEnabled || false}
            onChange={(e) => onChange('fallbackEnabled', e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Habilitar saída "Nenhuma das opções"
          </span>
        </label>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 ml-6">
          <strong className="text-slate-700 dark:text-slate-300">Desativado:</strong> se o usuário digitar algo inválido, a pergunta é repetida automaticamente até ele escolher uma opção válida.<br />
          <strong className="text-slate-700 dark:text-slate-300">Ativado:</strong> se o usuário digitar algo que não corresponde a nenhuma opção, o fluxo segue pela saída "Outro" ao invés de repetir a pergunta.
        </p>
        {config.fallbackEnabled && (
          <div className="mt-2 ml-6">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Mensagem de fallback (opcional)
            </label>
            <Input
              placeholder="Ex: Opção não reconhecida..."
              value={config.fallbackMessage || ''}
              onChange={(e) => onChange('fallbackMessage', e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 p-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            checked={groupingEnabled}
            onChange={(e) => onChange('messageGroupingEnabled', e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Agrupar mensagens neste bloco
        </label>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          Quando ativo, o bot espera alguns segundos e junta mensagens antes de validar a resposta da pergunta.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-slate-600 dark:text-slate-300">Janela</span>
          <input
            type="number"
            min={1}
            max={120}
            value={Number.isFinite(Number(config.messageGroupingWindowSeconds)) ? Number(config.messageGroupingWindowSeconds) : 5}
            onChange={(e) => onChange('messageGroupingWindowSeconds', Math.max(1, Math.min(120, Number(e.target.value) || 5)))}
            disabled={!groupingEnabled}
            className="w-24 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm disabled:opacity-50"
          />
          <span className="text-xs text-slate-600 dark:text-slate-300">segundos</span>
        </div>
      </div>
    </div>
  );
};
